/**
 * „Empfänger aus Akte wählen" im E-Mail-Fenster (Kundenfeedback Kanzlei Polz
 * 24.10.2026, IV.12 und X).
 *
 *  - Nur wer eine E-Mail-Adresse hat, ist wählbar. Die Mitarbeitenden des
 *    eigenen Unternehmens (ohne Adresse) erscheinen nicht mehr – zur Akte
 *    gehören die Beteiligten, die Sachbearbeiter und ggf. die beteiligte
 *    Abteilung, sofern sie mit E-Mail angelegt ist.
 *  - Alle Versicherungen der Akte sind wählbar: gegnerische Haftpflicht,
 *    Kasko/Teilkasko des Kunden, Rechtsschutz, GAP, Schutzbrief, Auslands-
 *    schadenschutz. Die Adresse kommt – wie auf der Beteiligten-Karte – aus
 *    den Falldaten oder ersatzweise aus dem Versicherungsverzeichnis.
 *  - Beschriftung deutsch, Name zuerst: „HUK-COBURG … – Gegnerische
 *    Haftpflichtversicherung" statt „opponentInsurance (HUK-COBURG …)".
 *
 * Reine Funktionen ohne Vue; die Daten liefert der Dialog.
 */
import { participantRolesLabel } from '@/utils/organizationRoles'
import { rsvDetailsApply } from '@/utils/rsv'

/** Eintrag aus GET /cases/{id}/involved (nur die hier genutzten Felder). */
export interface InvolvedLike {
  type?: string | null
  status?: string | null
  name?: string | null
  email?: string | null
  roles?: unknown
  companyName?: string | null
  departmentName?: string | null
  removedAt?: string | null
}

/** Treffer im Versicherungsverzeichnis (useInsuranceDirectory.matchInsurance). */
export interface InsuranceMatch {
  name?: string | null
  email?: string | null
}

export type InsuranceLookup = (name: string) => InsuranceMatch | null

export interface InsuranceSources {
  opponents?: Array<Record<string, unknown>> | null
  clients?: Array<Record<string, unknown>> | null
  vehicles?: Array<Record<string, unknown>> | null
  /** Kasko-Akte: Es gibt keine Gegenseite, eine gespeicherte Gegnerversicherung zählt nicht. */
  isKaskoCase?: boolean
  lookup?: InsuranceLookup | null
}

export interface RecipientCandidate {
  key: string
  email: string
  name: string
  /** Deutsche Rollenbezeichnung, z. B. „Gegnerische Haftpflichtversicherung". */
  role: string
  /** Gruppe für die Sortierung im Dialog. */
  group: 'client' | 'insurance' | 'participant' | 'team'
}

/** Technische Rollen der Beteiligtenliste → Anzeigename. */
const ROLE_LABELS: Record<string, string> = {
  opponentinsurance: 'Gegnerische Haftpflichtversicherung',
  opponent_insurance: 'Gegnerische Haftpflichtversicherung',
  sachbearbeiter: 'Sachbearbeiter',
  client: 'Kunde',
  mandant: 'Kunde',
  versicherung: 'Versicherung',
  beteiligter: 'Beteiligter',
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function text (v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

/** Erste gültige Adresse (Verzeichnis-Einträge enthalten teils „a@x.de; b@x.de"). */
export function firstEmail (raw: unknown): string {
  for (const part of text(raw).split(/[;,\s]+/)) {
    const candidate = part.trim().toLowerCase()
    if (EMAIL_PATTERN.test(candidate)) {
      return candidate
    }
  }
  return ''
}

/** Name bereinigen: „Firma <adresse>" → „Firma"; eine reine Adresse bleibt leer. */
function displayName (raw: unknown, email: string): string {
  const name = text(raw).replace(/\s*<[^>]*>\s*$/, '').trim()
  if (!name || name.toLowerCase() === email) {
    return ''
  }
  return name
}

/** Rollenbezeichnung eines Beteiligten, deutsch. */
export function involvedRoleLabel (p: InvolvedLike): string {
  const type = text(p.type).toLowerCase()
  if (type === 'mandant') {
    return 'Kunde'
  }
  if (ROLE_LABELS[type] && type !== 'sachbearbeiter') {
    return ROLE_LABELS[type]
  }
  const roles = (Array.isArray(p.roles) ? p.roles : []).map(r => {
    const raw = text(r)
    return ROLE_LABELS[raw.toLowerCase()] || raw
  })
  const roleText = participantRolesLabel(roles, '')
  if (type === 'sachbearbeiter') {
    return roleText ? `Sachbearbeiter (${roleText})` : 'Sachbearbeiter'
  }
  return roleText || 'Beteiligter'
}

function groupOf (p: InvolvedLike): RecipientCandidate['group'] {
  const type = text(p.type).toLowerCase()
  if (type === 'mandant') {
    return 'client'
  }
  if (type === 'sachbearbeiter') {
    return 'team'
  }
  if (type === 'opponentinsurance') {
    return 'insurance'
  }
  return 'participant'
}

/** Beteiligte mit E-Mail (ohne ehemalige). */
export function involvedCandidates (involved: InvolvedLike[] | null | undefined): RecipientCandidate[] {
  const out: RecipientCandidate[] = []
  for (const p of involved || []) {
    if (!p || p.removedAt) {
      continue
    }
    const email = firstEmail(p.email)
    if (!email) {
      continue
    }
    out.push({
      key: `e:${email}`,
      email,
      name: displayName(p.name, email) || displayName(p.companyName, email),
      role: involvedRoleLabel(p),
      group: groupOf(p),
    })
  }
  return out
}

interface InsuranceSpec {
  key: string
  role: string
  name: string
  email?: unknown
}

function vehicleInsuranceRole (insuranceType: string): string {
  if (insuranceType === 'Vollkasko' || insuranceType === 'Teilkasko') {
    return `${insuranceType}versicherung (Kunde)`
  }
  return insuranceType ? `Versicherung des Kunden (${insuranceType})` : 'Versicherung des Kunden'
}

function yes (v: unknown): boolean {
  return v === true || v === 'yes' || v === 1 || v === '1' || v === 'true'
}

/** Alle Versicherungen der Akte (Gegner, Kunde, Fahrzeuge) als Rohangaben. */
function insuranceSpecs (src: InsuranceSources): InsuranceSpec[] {
  const specs: InsuranceSpec[] = []
  if (!src.isKaskoCase) {
    for (const o of src.opponents || []) {
      if (!o) {
        continue
      }
      specs.push({
        key: `opp-${text(o.id)}`,
        role: 'Gegnerische Haftpflichtversicherung',
        name: text(o.insuranceName),
        email: o.insuranceEmail,
      })
      if (text(o.adjusterName) || text(o.adjusterEmail)) {
        specs.push({
          key: `adj-${text(o.id)}`,
          role: 'Regulierungsbeauftragter',
          name: text(o.adjusterName),
          email: o.adjusterEmail,
        })
      }
    }
  }
  for (const c of src.clients || []) {
    // Kundenfeedback Kanzlei Polz 27.10.2026 (II): bei RSV „Nein"/„Unklar" keine RSV als Empfänger.
    if (!c || !rsvDetailsApply(c.legalExpenseInsuranceExists)) {
      continue
    }
    specs.push({
      key: `rsv-${text(c.id)}`,
      role: 'Rechtsschutzversicherung',
      name: text(c.legalExpenseInsuranceName),
    })
  }
  for (const v of src.vehicles || []) {
    if (!v) {
      continue
    }
    specs.push({
      key: `veh-${text(v.id)}`,
      role: vehicleInsuranceRole(text(v.insuranceType)),
      name: text(v.insuranceName),
    })
    if (yes(v.gapInsuranceAvailable) || text(v.gapInsuranceName)) {
      specs.push({ key: `gap-${text(v.id)}`, role: 'GAP-Versicherung (Kunde)', name: text(v.gapInsuranceName) })
    }
    if (yes(v.protectionBriefInsuranceAvailable) || text(v.protectionBriefInsuranceName)) {
      specs.push({ key: `prot-${text(v.id)}`, role: 'Schutzbrief (Kunde)', name: text(v.protectionBriefInsuranceName) })
    }
    if (yes(v.foreignProtectionInsuranceAvailable) || text(v.foreignProtectionInsuranceName)) {
      specs.push({
        key: `foreign-${text(v.id)}`,
        role: 'Auslandsschadenschutzversicherung (Kunde)',
        name: text(v.foreignProtectionInsuranceName),
      })
    }
  }
  return specs
}

/**
 * Versicherungen mit E-Mail: Adresse aus den Falldaten, sonst aus dem
 * Versicherungsverzeichnis (gleiche Quelle wie die Beteiligten-Karte).
 */
export function insuranceCandidates (src: InsuranceSources): RecipientCandidate[] {
  const out: RecipientCandidate[] = []
  for (const spec of insuranceSpecs(src)) {
    if (!spec.name && !spec.email) {
      continue
    }
    const match = spec.name && src.lookup ? src.lookup(spec.name) : null
    const email = firstEmail(spec.email) || firstEmail(match?.email)
    if (!email) {
      continue
    }
    out.push({
      key: `e:${email}`,
      email,
      name: spec.name || text(match?.name),
      role: spec.role,
      group: 'insurance',
    })
  }
  return out
}

const GROUP_ORDER: Record<RecipientCandidate['group'], number> = { client: 0, insurance: 1, participant: 2, team: 3 }

/**
 * Gesamtliste für den Dialog: eine Zeile je Adresse. Bei Dubletten gewinnt
 * die aussagekräftigere Rolle (Versicherung vor allgemeinem Beteiligten).
 */
export function buildRecipientCandidates (involved: InvolvedLike[] | null | undefined, insurances: InsuranceSources): RecipientCandidate[] {
  const byEmail = new Map<string, RecipientCandidate>()
  const all = [...involvedCandidates(involved), ...insuranceCandidates(insurances)]
  for (const c of all) {
    const existing = byEmail.get(c.email)
    if (!existing) {
      byEmail.set(c.email, c)
      continue
    }
    const preferNew = c.group === 'insurance' && existing.group !== 'insurance' && existing.group !== 'client'
    if (preferNew) {
      byEmail.set(c.email, { ...c, name: c.name || existing.name })
    } else if (!existing.name && c.name) {
      byEmail.set(c.email, { ...existing, name: c.name })
    }
  }
  // eslint-disable-next-line unicorn/no-array-sort -- Kopie; toSorted fehlt in der TS-Zielbibliothek
  return [...byEmail.values()].sort((a, b) => GROUP_ORDER[a.group] - GROUP_ORDER[b.group])
}

/** Zeilentext: Name zuerst, dann die Rolle („HUK-COBURG – Gegnerische Haftpflichtversicherung"). */
export function candidateLabel (c: Pick<RecipientCandidate, 'name' | 'role' | 'email'>): string {
  const name = text(c.name) || text(c.email)
  return c.role ? `${name} – ${c.role}` : name
}
